import React, { useEffect, useState } from "react";

/** Nextjs */
import dynamic from "next/dynamic";

/** MUI */
import { ArrowDropDown, Close, Launch, SmsFailedOutlined } from "@mui/icons-material";
import { Alert, Box, Button, Grid, IconButton, Popover, Typography, alpha, useMediaQuery } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

/** Redux */
import { useDispatch, useSelector } from "react-redux";
import { getPrediction, setLoadingModules } from "../../../_appStore/DB_Common";
import { getSelectedIndicators } from "../../../_appStore/DB_Indicators";
import {
    getSelectedTerritorialUnitsItemDefault,
    getSelectedTerritorialUnitsItems,
    setExpanded,
    setJTsForIndicators,
    setScrolled,
    setSelectedTerritorialUnitsItem,
    setSelectedTerritorialUnitsItemDefault,
} from "../../../_appStore/DB_JTs";

/** API */
import { getTerritorialUnitsFromAPI } from "../../../_APIs";

/** Helpers */
import clsx from "clsx";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import useHelper from "../../../_common/useHelper";

/** UI */
import Predictionist from "./Tools/Predictionist";
import ApplyButton from "./Tools/ApplyButton";
import AppTooltip from "@/src/_common/AppTooltip";

import TerritorialUnitsRangeSkeleton from "../skeletons/TerritorialUnitsRangeSkeleton";

const Preloader = dynamic(() => import("@/src/_common/Preloader"));
// const ProgressAPI = dynamic(() => import("@/src/_common/ProgressAPI"));
const SearchTree = dynamic(() => import("./Search/SearchTree"));
const UnitsTree = dynamic(() => import("./UnitsTree/Units_TreeView"));

const useStyles = makeStyles((theme) => ({
    buttonRoot: {
        minWidth: 240,
        justifyContent: "space-between",
        borderRadius: 6,
        fontSize: "1.11111111rem",
        textTransform: "none",
        padding: theme.spacing(1.25, 4),
        border: "0.5px solid #D2D6DA",
        color: "#000",
        fontWeight: 400,
        "@media (max-width: 1365px)": {
            fontSize: "1rem",
        },
        [theme.breakpoints.down("md")]: {
            minWidth: "90%",
            fontSize: "0.88888889rem",
            padding: theme.spacing(1, 1),
            height: "unset",
        },
    },
    buttonEndIcon: {
        [theme.breakpoints.down("sm")]: {
            marginLeft: 2,
        },
    },
    btnLabel: {
        textTransform: "none",
    },
    popoverRoot: {
        zIndex: "9998!important",
        [theme.breakpoints.down("sm")]: {
            zIndex: "9998!important",
        },
        // maxHeight: "9vh",
    },
    popoverRootFullsize: {
        position: "absolute!important",
        zIndex: "9998!important",
        backgroundColor: "rgba(0,0,0,0.666)",
    },
    popoverPaper: {
        ...theme.shape.boxedWrapper,
        width: 660,
        height: "100%",
        position: "relative",
        maxWidth: "calc(100vw - 12px)",
        height: "calc(100vh - 60px)",
        maxHeight: "unset",
        top: `16px!important`,
        padding: theme.spacing(1, 5, 2, 5),
        overflow: "hidden",
        [theme.breakpoints.down("sm")]: {
            maxHeight: "calc(100vh - 60px)",
            height: "100vh",
            padding: theme.spacing(2, 1, 1, 1),
            top: `6px!important`,
            left: "6px!important",
        },
    },
    popoverPaperFullsize: {
        ...theme.shape.boxedWrapper,
        width: 1280,
        height: "100%",
        position: "relative",
        maxWidth: "calc(100vw - 12px)",
        height: "calc(100vh - 32px)",
        maxHeight: "unset",
        padding: theme.spacing(2, 5, 2, 5),
        [theme.breakpoints.down("sm")]: {
            left: "6px!important",
            maxHeight: "calc(100vh - 180px)",
            padding: theme.spacing(2, 5, 2, 5),
        },
        top: `16px!important`,
        left: "50%!important",
        transform: "translateX(-50%)!important",
    },
    navbar: {
        display: "flex",
        width: "100%",
        alignItems: "center",
        justifyContent: "space-between",
    },
    dividerPrimary: {
        backgroundColor: theme.palette.primary.main,
        margin: theme.spacing(1.5, 0),
    },
    divider: {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        margin: theme.spacing(1, 0, 0, 0),
        width: "100%",
    },
    actionsbar: {
        display: "flex",
        width: "100%",
        alignItems: "center",
        justifyContent: "flex-end",
        margin: theme.spacing(0.5, 0),
        padding: theme.spacing(0, 2, 1, 2),
        position: "absolute",
        left: 0,
        bottom: 0,
        flexWrap: "wrap",
        "& > button": {
            minWidth: 140,
        },
        [theme.breakpoints.down("sm")]: {
            margin: theme.spacing(0, 0),
            "& button": {
                width: "100%",
                padding: theme.spacing(1, 2),
                height: "unset",
            },
        },
    },
    btnGroup: {
        margin: theme.spacing(1, 0, 2, 0),
        "& svg": {
            fontSize: "1rem",
        },
        "& button": {
            padding: theme.spacing(0.5, 1),
            fontSize: "0.675rem",
        },
    },
    boxedWrapper: {
        ...theme.shape.boxedWrapper,
    },
    sectionWrapper: {
        ...theme.shape.boxedWrapper,
        boxShadow: "unset",
        borderRadius: theme.spacing(0.5),
        position: "relative",
        padding: theme.spacing(1, 1, 1, 3),
        margin: theme.spacing(0, 0, 2, 0),
        "& > p": {
            position: "absolute",
            top: -12,
            padding: theme.spacing(0, 0.75),
            backgroundColor: "#FFF",
            color: "#aaadb1",
        },
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(1, 1),
        },
    },
    searchWrapper: {
        position: "relative",
        padding: theme.spacing(0, 0),
        margin: theme.spacing(-1, 0, 3, 0),
    },
    deLaunch: {
        transform: "rotate(180deg)",
    },
    alertBox: {
        position: "absolute",
        top: 16,
        right: 4,
        bottom: 4,
        left: 4,
        backgroundColor: "#fff",
        zIndex: 9999,
        padding: theme.spacing(1),
        textAlign: "center",
    },
    alertRoot: {
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(0.5, 1, 1, 1),
        },
    },
    alertMessage: {
        textAlign: "left",
        [theme.breakpoints.down("md")]: {
            fontSize: "0.75rem",
            textIndent: 24,
        },
    },
    alertIcon: {
        [theme.breakpoints.down("sm")]: {
            position: "absolute",
            left: 16,
            top: 12,
            fontSize: "1rem",
        },
    },
    loadingBox: {
        position: "absolute",
        top: 16,
        right: 4,
        bottom: 4,
        left: 4,
        background: "rgb(255,255,255)",
        background: "linear-gradient(180deg, rgba(255,255,255,0.97) 33%, rgba(255,255,255,0.75) 100%)",
        zIndex: 9998,
        paddingTop: theme.spacing(5),
        textAlign: "center",
    },
    loaderWrap: {
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: "#FFFFFF",
        // border: "1px solid #F00",
        zIndex: 13,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 20,
        padding: theme.spacing(0, 2),
    },
}));

const TerritorialUnitsRange = (props) => {
    const { onPredictionData, tableOrChart } = props;

    const { t } = useTranslation();
    const { translationFromJson, getAllIDs } = useHelper();
    const classes = useStyles();
    const dispatch = useDispatch();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
    // process.env.NODE_ENV === "development" &&
    //     console.log("%c TerritorialUnitsRange ", "background: #550; color: #FFF;");

    /** Redux states */
    const selectedJTs = useSelector(getSelectedTerritorialUnitsItems);
    const selectedJTsDefault = useSelector(getSelectedTerritorialUnitsItemDefault);
    const selectedIndicators = useSelector(getSelectedIndicators);
    // const prediction = useSelector(getPrediction);
    /** / */

    /** Component states */
    const [API_DATA, setAPI_DATA] = useState(null);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [count, setCount] = useState(0);
    const [ERROR_JTS, setERROR_JTS] = useState(false);
    const [EXPANDED_DEFAULT, setEXPANDED_DEFAULT] = useState(["33617"]);
    const [FULLSIZE, setFULLSIZE] = useState(false);
    const [JTS_SNAPSHOT, setJTS_SNAPSHOT] = useState(null);
    const [JTS_NEW, setJTS_NEW] = useState(JSON.parse(JSON.stringify(selectedJTs)));
    const [LEVELS, setLEVELS] = useState(null);
    const [LOADING_JTS, setLOADING_JTS] = useState(false);
    const [SEARCH_STRING, setSEARCH_STRING] = useState("");
    const [SEARCH_NOT_FOUND, setSEARCH_NOT_FOUND] = useState(false);
    const [SCROLLABLE, setSCROLLABLE] = useState(true);

    /** / */

    const open = Boolean(anchorEl);
    const id = open ? "db-jt-popover" : undefined;

    /** Handlers */
    /** - open JTs popover */
    const handleOpen = (event) => setAnchorEl(event.currentTarget);

    /** - close JTs popover and clear some component states */
    const handleClose = () => {
        setJTS_NEW(JTS_SNAPSHOT);
        setAnchorEl(null);
        setERROR_JTS(false);
        setJTS_SNAPSHOT(null);
        setSCROLLABLE(true);
        setFULLSIZE(false);
        setSEARCH_STRING("");

        dispatch(setScrolled(null));
    };

    /** - fullsize  */
    const handleFullsize = () => setFULLSIZE(!FULLSIZE);

    /** - undo current changes made by user */
    const handleReset = () => {
        setJTS_NEW(selectedJTsDefault);
        setSEARCH_STRING("ReSEt");
        dispatch(setExpanded(EXPANDED_DEFAULT));
    };

    /** - provide current changes of selected JTs made by user */
    const handleOnChangeJTs = ({ newJTs }) => {
        setJTS_NEW(newJTs);
    };

    /** - apply changes made by user */
    const handleApply = () => {
        dispatch(setSelectedTerritorialUnitsItem(JTS_NEW));
        handleClose();
    };

    const handleOnSearchString = (args) => {
        setSEARCH_STRING(args);
    };

    const handleOnSearchNotFound = (args) => {
        setSEARCH_NOT_FOUND(args);
    };

    /** Effects */
    /** - loading modules */
    useEffect(() => {
        dispatch(setLoadingModules({ key: "jt", value: LOADING_JTS }));
    }, [LOADING_JTS]);

    /** - set state of selected on before changes */
    useEffect(() => {
        if (open && !JTS_SNAPSHOT) {
            setJTS_SNAPSHOT(JSON.parse(JSON.stringify(selectedJTs)));
        }
    }, [JTS_SNAPSHOT, open]);

    /** - prepare state of selected JTs for current changes */
    useEffect(() => {
        setJTS_NEW(JSON.parse(JSON.stringify(selectedJTs)));
    }, [selectedJTs]);

    /** - count selected */
    useEffect(() => {
        setCount(selectedJTs.length?.toString());
    }, [selectedJTs]);

    /** - Collect JTs data from API */
    useEffect(() => {
        if (selectedIndicators.length && !ERROR_JTS) {
            setLOADING_JTS(true);
            const IDs = selectedIndicators.map((i) => i.indicator_id);

            getTerritorialUnitsFromAPI(IDs).then((result) => {
                if (result.message && result.message.type === "Error") {
                    setERROR_JTS(result);
                    setLOADING_JTS(false);
                } else {
                    /** Set which indicators is this data for */
                    dispatch(setJTsForIndicators(IDs));

                    // const ALL_IDS = getAllIDs(result)?.map((r) => ({ ...r, children: [] }));
                    const ALL_IDS = getAllIDs(result)
                        ?.map((r) => ({ ...r, children: [] }))
                        ?.toSorted((a, b) => b.czy_aktywny - a.czy_aktywny);

                    const _levels = _.uniqBy(
                        ALL_IDS?.map((a) => ({
                            poziom_id: a.poziom_id,
                            poziom_kod: a.poziom_kod,
                            poziom_nazwa: a.poziom_nazwa,
                            poziom_nazwa_ang: a.poziom_nazwa_ang,
                            show: true,
                            active: a.poziom_id < 14,
                            apiActive: a.czy_aktywny,
                        })),
                        "poziom_id"
                    );

                    /** Get checked in API */
                    const checkedJTs = ALL_IDS.filter((c) => c.czy_zaznaczony);
                    const currentSelected = selectedJTs?.filter((s) =>
                        ALL_IDS.map((c) => c.id_pozycja).includes(s.id_pozycja)
                    );

                    if (checkedJTs.length) {
                        dispatch(
                            setSelectedTerritorialUnitsItem(
                                _.uniqBy([...checkedJTs, ...currentSelected], (v) => JSON.stringify([v.id_pozycja]))
                            )
                        );
                        dispatch(setSelectedTerritorialUnitsItemDefault(checkedJTs));
                    }

                    const _expandedDefault = ALL_IDS?.filter((a) => a.poziom_kod < "2")?.map((a) => a.id);

                    setAPI_DATA(result);

                    setEXPANDED_DEFAULT(_expandedDefault);
                    dispatch(setExpanded(_expandedDefault));

                    setLEVELS(_levels);
                    setERROR_JTS(false);
                    setLOADING_JTS(false);
                }
            });
        }
    }, [selectedIndicators]);

    return (
        <>
            {!!LOADING_JTS ? (
                <Box className={classes.loaderWrap}>
                    {/* <ProgressAPI preloader={true} /> */}
                    {/* <Preloader type={"linear"} /> */}
                    <TerritorialUnitsRangeSkeleton />
                </Box>
            ) : null}

            {!!LOADING_JTS && !anchorEl ? (
                <span style={{ maxHeight: 53 }}>
                    <Preloader type={"linear"} linesCount={2} />
                </span>
            ) : (
                <>
                    {ERROR_JTS ? (
                        <Alert severity={"error"} sx={{ width: "100%" }} variant={"outlined"}>
                            {translationFromJson(ERROR_JTS.message, "text") || t("Nieznany błąd")}
                        </Alert>
                    ) : (
                        <Button
                            variant={"outlined"}
                            color={"inherit"}
                            endIcon={<ArrowDropDown />}
                            className={classes.buttonRoot}
                            classes={{ label: classes.btnLabel, endIcon: classes.buttonEndIcon }}
                            onClick={handleOpen}
                            fullWidth
                        >
                            <span>
                                {t("Jednostka terytorialna")}
                                {!!count && <span>&nbsp;{`[${count}]`}</span>}
                            </span>
                        </Button>
                    )}

                    <Popover
                        id={id}
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "center",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "center",
                        }}
                        classes={{
                            root: clsx(classes.popoverRoot, FULLSIZE && classes.popoverRootFullsize),
                            paper: clsx(classes.popoverPaper, FULLSIZE && classes.popoverPaperFullsize),
                        }}
                    >
                        <Box className={classes.navbar} sx={{ mb: FULLSIZE ? 3 : 0 }}>
                            <span />

                            {!!FULLSIZE && (
                                <Typography
                                    variant={"body1"}
                                    sx={{ fontWeight: 600, flexGrow: 1, fontSize: "1.33333333rem" }}
                                >
                                    {t("Jednostka terytorialna")}
                                    {!!count && <span>&nbsp;{`[${count}]`}</span>}
                                </Typography>
                            )}

                            <span>
                                {!!isMobile || (
                                    <AppTooltip
                                        title={
                                            FULLSIZE
                                                ? t("Otwórz wybór jednostki terytorialnej w małym oknie")
                                                : t("Otwórz wybór jednostki terytorialnej w dużym oknie")
                                        }
                                        arrow
                                        placement={"top"}
                                        type={"dark"}
                                        classes={{ tooltip: classes.tooltip }}
                                    >
                                        <IconButton
                                            size={"small"}
                                            onClick={handleFullsize}
                                            aria-label={
                                                FULLSIZE
                                                    ? t("Otwórz wybór jednostki terytorialnej w małym oknie")
                                                    : t("Otwórz wybór jednostki terytorialnej w dużym oknie")
                                            }
                                        >
                                            <Launch
                                                fontSize={"small"}
                                                color={"primary"}
                                                className={clsx(FULLSIZE && classes.deLaunch)}
                                            />
                                        </IconButton>
                                    </AppTooltip>
                                )}
                                <AppTooltip
                                    title={t("Zamknij okno")}
                                    arrow
                                    placement={"top"}
                                    type={"dark"}
                                    classes={{ tooltip: classes.tooltip }}
                                >
                                    <IconButton size={"small"} onClick={handleClose} aria-label={t("Zamknij okno")}>
                                        <Close fontSize={"small"} color={"primary"} />
                                    </IconButton>
                                </AppTooltip>
                            </span>
                        </Box>

                        <Grid container rowSpacing={1} sx={{ marginTop: 1 }}>
                            <Predictionist
                                JTS_NEW={JTS_NEW}
                                tableOrChart={tableOrChart}
                                onPredictionData={onPredictionData}
                            />

                            {!!JTS_NEW?.length ? null : (
                                <Grid item xs={12}>
                                    <Alert severity={"error"} sx={{ mb: 4 }}>
                                        {t("Należy wybrać co najmniej jedną jednostkę terytorialną")}
                                        {"."}
                                    </Alert>
                                </Grid>
                            )}

                            <Grid item xs={12} className={classes.searchWrapper}>
                                <SearchTree
                                    searchData={[]}
                                    onSearch={() => {}}
                                    onSearchString={(args) => handleOnSearchString(args)}
                                    searchString={SEARCH_STRING}
                                    searchNotFound={SEARCH_NOT_FOUND}
                                />
                            </Grid>

                            <Grid item xs={12} className={classes.sectionWrapper}>
                                {SEARCH_STRING?.length && SEARCH_NOT_FOUND === 0 ? (
                                    <Box className={classes.alertBox}>
                                        <Alert
                                            severity={"error"}
                                            classes={{
                                                root: classes.alertRoot,
                                                message: classes.alertMessage,
                                                icon: classes.alertIcon,
                                            }}
                                        >
                                            {t(
                                                "Brak jednostek terytorialnych pasujących do wprowadzonego ciągu znaków{{dot}}",
                                                {
                                                    dot: ".",
                                                }
                                            )}
                                        </Alert>
                                    </Box>
                                ) : null}

                                {LOADING_JTS ? (
                                    <Box className={classes.loadingBox}>
                                        {LOADING_JTS?.message ? (
                                            <Box
                                                dangerouslySetInnerHTML={{
                                                    __html: LOADING_JTS?.message,
                                                }}
                                                component={"p"}
                                                sx={{ mb: 2, fontSize: "0.77777778rem", color: "rgba(0, 0, 0, 0.5)" }}
                                            />
                                        ) : null}
                                        {/* <Preloader type={"linear"} /> */}
                                        
                                        <TerritorialUnitsRangeSkeleton />
                                    </Box>
                                ) : null}

                                <Typography variant={"caption"} component={"p"}>
                                    {t("Wybierz jednostkę")}
                                    {JTS_NEW?.length ? ` (${JTS_NEW?.length})` : ""}
                                </Typography>

                                <UnitsTree
                                    onChangeJTs={(newJTs) => handleOnChangeJTs({ newJTs })}
                                    selectedJTs={JTS_NEW}
                                    levels={LEVELS}
                                    defaultExpanded={EXPANDED_DEFAULT}
                                    fullsize={FULLSIZE}
                                    // hasAlert={!!prediction?.table?._errMessageShort || !JTS_NEW?.length}
                                    hasAlert={!JTS_NEW?.length}
                                    searchString={SEARCH_STRING}
                                    treeData={API_DATA}
                                    onSearchNotFound={(args) => handleOnSearchNotFound(args)}
                                    // loading={LOADING_JTS}
                                    // error={ERROR_JTS}
                                    // scrollable={SCROLLABLE}
                                    // onScrollable={(args) => handleScrollable(args)}
                                />
                            </Grid>
                        </Grid>

                        <div className={classes.actionsbar}>
                            <Button onClick={() => handleReset({ close: true })} color={"primary"} size={"small"}>
                                {t("Zresetuj")}
                            </Button>

                            <ApplyButton JTS_NEW={JTS_NEW} JTS_SNAPSHOT={JTS_SNAPSHOT} handleApply={handleApply} />
                        </div>
                    </Popover>
                </>
            )}
        </>
    );
};

export default TerritorialUnitsRange;
